import { Dimensions, StyleSheet } from 'react-native';
import { SPACING } from '@/constants/shared/theme/spacing';

// Screen dimensions
export const { width: SCREEN_WIDTH } = Dimensions.get('window');

// Header animation constants
export const MIN_HEADER_HEIGHT = 64;
export const BEGIN_ANIMATION_HEIGHT = 128; // 64 * 2, matching iOS
export const PLACEHOLDER_HEIGHT = 180;
export const CARD_OVERLAY_HEIGHT = 28;

// iOS padding scale — now backed by the shared SPACING scale.
// Off-scale legacy steps (paddingHalf:2, padding7:28) stay literals.
/** @deprecated use useSpacing() from '@/hooks/shared/useSpacing' */
export const PADDING = {
  paddingHalf: 2,
  padding1: SPACING.xs,
  padding2: SPACING.sm,
  padding3: SPACING.md,
  padding4: SPACING.lg,
  padding5: SPACING.xl,
  padding6: SPACING['2xl'],
  padding7: 28,
  padding8: SPACING['3xl'],
  padding10: SPACING['4xl'],
  padding12: SPACING['5xl'],
} as const;

// Colors (exact iOS values from UIColor+Extension.swift)
export const COLORS = {
  background: '#F2F2F7', // iOS .Background.primary
  backgroundSecondary: '#E3E3E3', // iOS .Background.secondary (gray100 - same as cardBackground)
  cardBackground: '#E3E3E3', // iOS .Card.primary (gray100) - matches iOS exactly
  cardBorder: '#C8C8C8', // iOS .Border.card (gray200) - matches iOS exactly
  primary: '#007AFF',
  textPrimary: '#171717', // iOS .Text.primary: resioBlack #171717 (exact match)
  textSubtitle: '#8E8E93',
  textDate: '#5E5E5E', // iOS .Text.date: gray600 #5E5E5E (exact match)
  separator: '#C7C7CC',
  iconBackground: '#E5F2FF',
  moreResourcesBackground: '#F2F2F7',
  red: '#FF3B30',
  shadow: '#000',
  // Bulletin icon colors (matching iOS CircleIcon colors)
  circleIconBackground: '#E5F2FF', // Default icon background
  circleIconPrimaryTint: '#007AFF', // Default icon tint
  circleIconNotificationBackground: '#FF3B30', // Notification background
  circleIconSecondaryTint: '#FFFFFF', // Notification icon tint
} as const;

/**
 * Local home/card typography.
 *
 * NOT orphaned — `ContactOfficeScreen`, `MoreResourcesScreen` and
 * `ResourceDetailScreen` import this at module scope inside
 * `StyleSheet.create`, so removing it is a runtime crash, not a type-only
 * break. It was deleted in PR #209 on a mistaken "0 importers" reading and
 * restored here.
 *
 * Distinct from the canonical scale: `sectionTitle` is 18/700 here vs 17/600
 * in `@/constants/shared/theme/typography`. Migrating these three screens onto
 * the canonical scale is a deliberate visual change and is NOT done here.
 */
export const TYPOGRAPHY = {
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700' as const,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: '700' as const,
  },
  cardDescription: {
    fontSize: 13,
    fontWeight: '400' as const,
  },
  bulletinText: {
    fontSize: 13,
    fontWeight: '500' as const,
  },
} as const;

// Shadow styles (matches iOS CardView exactly)
/** @deprecated use useShadow() from '@/hooks/shared/useShadow' (0 external importers; kept for CARD_STYLES) */
export const SHADOW = {
  card: {
    shadowColor: COLORS.shadow, // iOS: color .black
    shadowOffset: { width: 1, height: 2 }, // iOS: x: 1, y: 2
    shadowOpacity: 0.15, // iOS: alpha: 0.15
    shadowRadius: 6, // iOS: blur: 6
    elevation: 4, // Android equivalent
  },
  separator: {
    shadowColor: COLORS.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
  },
  cardOverlay: {
    shadowColor: COLORS.shadow,
    shadowOffset: { width: 0, height: -1 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 5,
  },
} as const;

// Icon sizes
export const ICON_SIZES = {
  small: 20,
  medium: 24,
  large: 40,
  jumbo: 64, // For empty states (matching iOS jumbo size)
} as const;

// Animation values
export const ANIMATION = {
  pullDownRange: 200,
  scaleMax: 1.5,
  scrollEventThrottle: 16,
  logoSize: { width: 88, height: 25 },
} as const;

// =============================================================================
// REDESIGN TYPOGRAPHY & SPACING (Figma Redesign 2026)
// =============================================================================

/** @deprecated use useTypography() from '@/hooks/shared/useTypography' */
export { TYPOGRAPHY as REDESIGN_TYPOGRAPHY } from '@/constants/shared/theme/typography';

export const REDESIGN_SPACING = {
  horizontalPadding: 24,
  cardPadding: 24,
  cardBorderRadius: 20,
  quickActionRadius: 16,
  sectionGap: 12,
  contentGap: 16,
  gridGap: 12,
  iconSize: {
    quickAction: 42,
    quickActionIcon: 20,
    activityIcon: 40,
    activityIconInner: 18,
    resourceIcon: 36,
    resourceIconInner: 18,
  },
  buttonHeight: 52,
  buttonHeightSmall: 42,
  buttonRadius: 10,
} as const;

// Standard card styles (matches iOS CardView exactly)
export const CARD_STYLES = {
  backgroundColor: COLORS.cardBackground,
  borderRadius: 14, // iOS CornerRadius.card = 14.0
  borderWidth: StyleSheet.hairlineWidth, // iOS BorderWidth.borderSmallest (1.0 / UIScreen.main.scale)
  borderColor: COLORS.cardBorder, // iOS .Border.card = gray200 (#C8C8C8)
  ...SHADOW.card,
} as const;